import { get, orderBy } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { LocationGroupScheduleComponentContext } from 'src/components/context';
import {
  SortMenuOption,
  SortOptions,
} from 'src/components/subHeader/SubHeaderWithSorting.interface';
import { RowsPerPageOptions } from 'src/components/table/CustomTableWithPagination/TableControlsHeader/TableControlsHeader.interface';
import {
  useGetDefaultAssemblies,
  useGetLocationGroups,
  useGetLocations,
  useGetLocationsCountByGroup,
  useGetPaginatedLocations,
  useParamQuery,
} from 'src/hooks';
import { ILocationGroupWithLocations } from 'src/interfaces';
import { escapeCsvValue } from 'src/utils';

export const useReviewPageContent = (
  earliestScheduleStartDate?: string | null,
) => {
  const contexts = useContext(LocationGroupScheduleComponentContext);
  const [mainExpandedRowIndex, setMainExpandedRowIndex] = useState<number>(-1);
  const [paginatedLocationGroups, setPaginatedLocationGroups] = useState<
    ILocationGroupWithLocations[]
  >([]);
  const { onChangeParams, getParamsValue } = useParamQuery({
    sort: ['name'],
    page: ['1'],
    pageSize: ['20'],
  });
  const rowsPerPage = Number(getParamsValue('pageSize')?.[0]) || 20;
  const currentPage = Number(getParamsValue('page')?.[0]) || 1;

  const selectedLocationGroups =
    contexts.selectedLocationGroupId !== undefined
      ? Array.from(contexts.selectedLocationGroups).concat([
          contexts.selectedLocationGroupId,
        ])
      : Array.from(contexts.selectedLocationGroups);

  const { data: locationGroups, isLoading: locationGroupsLoadingFromQuery } =
    useGetLocationGroups(
      [`id.list=${selectedLocationGroups.join('|')}`],
      Boolean(selectedLocationGroups.length),
    );

  const totalLocationGroups = locationGroups?.length ?? 0;
  const isSingleLocationGroup = totalLocationGroups === 1;

  const multiGroupLocationsFilters = useMemo(() => {
    if (isSingleLocationGroup || selectedLocationGroups.length <= 1) {
      return [];
    }

    return [`location_group_id=${selectedLocationGroups.join('|')}`];
  }, [isSingleLocationGroup, selectedLocationGroups]);

  const { data: allLocationsData = [], isLoading: locationsLoading } =
    useGetLocations(multiGroupLocationsFilters);

  const locationGroupsWithLocations = useMemo(() => {
    if (!locationGroups) return [];
    if (isSingleLocationGroup) return locationGroups;

    return locationGroups.map((group) => ({
      ...group,
      locations: allLocationsData.filter((loc) =>
        loc.location_group_id.includes(group.id),
      ),
    }));
  }, [locationGroups, allLocationsData, isSingleLocationGroup]);

  const { data: locationGroupDefaultAssemblies = [] } = useGetDefaultAssemblies(
    [
      `location_group_id.list=${selectedLocationGroups.join('|')}`,
      `daypart_id=${contexts?.daypartId}`,
      `is_expired=false`,
    ],
    Boolean(selectedLocationGroups.length),
    '',
    true,
    earliestScheduleStartDate ?? undefined,
  );

  const singleGroupId = locationGroups?.[0]?.id;

  const { data: singleGroupLocationsCount = 0 } = useGetLocationsCountByGroup(
    [`locationGroups.id=${singleGroupId}`],
    isSingleLocationGroup && !!singleGroupId,
  );

  const { data: singleGroupLocationsData } = useGetPaginatedLocations(
    [
      `location_group_id.list=${singleGroupId}`,
      `pageSize=${rowsPerPage}`,
      `page=${currentPage}`,
    ],
    undefined,
    { enabled: isSingleLocationGroup && !!singleGroupId },
  );

  const totalCount = isSingleLocationGroup
    ? singleGroupLocationsCount || 1
    : selectedLocationGroups.length || 1;

  const handlePageChange = useCallback(
    (_: React.ChangeEvent<unknown>, value: number) => {
      if (value !== 1) {
        onChangeParams('page', [value.toString()]);
      } else {
        onChangeParams('page', []);
      }
    },
    [onChangeParams],
  );

  const handleRowsPerPageChange = useCallback(
    (newRowsPerPage: number) => {
      if (newRowsPerPage !== 20) {
        onChangeParams('pageSize', [newRowsPerPage.toString()]);
      } else {
        onChangeParams('pageSize', []);
      }
      onChangeParams('page', []);
    },
    [onChangeParams],
  );

  const selectedSortOption: SortMenuOption = useMemo(
    () => ({
      option: getParamsValue('sort')[0]?.replace('-', '') as SortOptions,
      order: getParamsValue('sort')[0]?.includes('-') ? 'desc' : 'asc',
    }),
    [getParamsValue],
  );

  const onSortChangeHandle = (selectedOption: {
    option: SortOptions;
    order: 'desc' | 'asc';
  }) => {
    onChangeParams('sort', [
      `${selectedOption.order === 'desc' ? '-' : ''}${selectedOption.option}`,
    ]);
  };

  const handleMainRowExpand = useCallback((index: number) => {
    setMainExpandedRowIndex((prev) => (prev === index ? -1 : index));
  }, []);

  const generateExportData = (
    componentNames: string[],
    positionString: string,
  ) => {
    if (!locationGroupsWithLocations?.length) return [];

    const data: any[] = [];

    locationGroupsWithLocations.forEach((locationGroup) => {
      const defaultAssembly = locationGroupDefaultAssemblies
        .filter((item) => item.locationGroupId === locationGroup.id)
        .sort((a, b) => a.startDate.localeCompare(b.startDate))[0];

      data.push({
        name: escapeCsvValue(locationGroup.name),
        daypart: escapeCsvValue(contexts.daypartName || ''),
        defaultAssembly: escapeCsvValue(
          defaultAssembly?.assemblyData?.name || '',
        ),
        scheduleName: escapeCsvValue(contexts.scheduleName || ''),
        position: escapeCsvValue(positionString),
        components: escapeCsvValue(componentNames.join(', ')),
      });
    });

    return data;
  };

  const isLocationGroupsLoading =
    contexts.selectedLocationGroups.size === 0
      ? locationGroupsLoadingFromQuery
      : false;

  // Reset pageSize when first loaded
  useEffect(() => {
    onChangeParams('pageSize', [String(RowsPerPageOptions.TWENTY)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const sortedLocationGroups = orderBy(
      locationGroupsWithLocations ?? [],
      [
        (item) => {
          const value = get(item, selectedSortOption.option);

          return typeof value === 'string' ? value.toLowerCase() : value;
        },
      ],
      [selectedSortOption.order],
    );
    if (sortedLocationGroups.length === 0) {
      setPaginatedLocationGroups([]);

      return;
    }

    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    if (isSingleLocationGroup) {
      const [singleLocationGroup] = sortedLocationGroups;
      setPaginatedLocationGroups([
        {
          ...singleLocationGroup,
          locations: singleGroupLocationsData?.locations ?? [],
        },
      ]);

      return;
    }

    setPaginatedLocationGroups(
      sortedLocationGroups.slice(startIndex, endIndex),
    );
  }, [
    rowsPerPage,
    currentPage,
    locationGroupsWithLocations,
    selectedSortOption,
    isSingleLocationGroup,
    singleGroupLocationsData,
  ]);

  return {
    totalCount,
    totalLocationGroups,
    isLocationGroupsLoading,
    isLocationsLoading: locationsLoading,
    contexts,
    locationGroups: paginatedLocationGroups,
    rowsPerPage,
    currentPage,
    totalPages: Math.ceil(totalCount / rowsPerPage),
    handlePageChange,
    handleRowsPerPageChange,
    selectedSortOption,
    onSortChangeHandle,
    mainExpandedRowIndex,
    handleMainRowExpand,
    generateExportData,
    locationGroupDefaultAssemblies,
  };
};
